import { Observable } from 'rxjs';

import {
	GateOffBehavior,
	LightBehavior,
	LightColor,
	LightingRingStyle,
	Response,
	DeviceTransmitterLightingBehavior
} from '@shure/shared/angular/data-access/system-api/models';

import { LightsDevice } from './models/device';

/**
 * `LightsApiService` is an abstraction for **all** the functionality
 * required by the lights feature.
 */
export abstract class LightsApiService {
	/**
	 * Get a device by id.
	 */
	public abstract getDevice$(deviceId: string): Observable<LightsDevice>;

	/**
	 * Update device LED brightness.
	 */
	public abstract setBrightness(deviceId: string, value: number): Observable<Response<void, string>>;

	/**
	 * Turn device network jack LEDs on or off.
	 */
	public abstract toggleNetworkJackLeds(deviceId: string, isOn: boolean): Observable<Response<void, string>>;

	/**
	 * Turn device audio level LEDs on or off.
	 */
	public abstract toggleAudioLightingActivityLeds(
		deviceId: string,
		isOn: boolean
	): Observable<Response<void, string>>;

	/**
	 * Set light ring style.
	 */
	public abstract setLightRingStyle(
		deviceId: string,
		ringStyle: LightingRingStyle
	): Observable<Response<void, string>>;

	/**
	 * Toggle automix gating.
	 */
	public abstract toggleAutomixGating(deviceId: string, enabled: boolean): Observable<Response<void, string>>;

	/**
	 * Set gate off behavior mode
	 */
	public abstract setGateOffBehavior(deviceId: string, behavior: GateOffBehavior): Observable<Response<void, string>>;

	/**
	 * Update device LED color when muted.
	 */
	public abstract setMuteColor(deviceId: string, color: LightColor): Observable<Response<void, string>>;

	/**
	 * Update device LED color when not muted.
	 */
	public abstract setUnmuteColor(deviceId: string, color: LightColor): Observable<Response<void, string>>;

	/**
	 * Clear device LED errors.
	 */
	public abstract clearLedErrors(deviceId: string): Observable<Response<void, string>>;

	/**
	 * Update device LED behavior when not muted.
	 */
	public abstract setUnmuteBehavior(deviceId: string, behavior: LightBehavior): Observable<Response<void, string>>;

	/**
	 * Update device LED behavior when muted.
	 */
	public abstract setMuteBehavior(deviceId: string, behavior: LightBehavior): Observable<Response<void, string>>;

	/**
	 * Update transmitter device LED behavior.
	 */
	public abstract setTransmitterLightingBehavior(
		deviceId: string,
		behavior: DeviceTransmitterLightingBehavior
	): Observable<Response<void, string>>;
}
